import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { TableViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/TableViewColumnDefinition'
import { StickyStyle } from '@modules/common/models'
import scrollbarSize from 'dom-helpers/esm/scrollbarSize'
import isEmpty from 'lodash/isEmpty'
import memoize from 'memoize-one'
import { useObserver } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { VariableSizeList as List } from 'react-window'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { TableViewStoreContext } from '../StandardTableView'
import { TableBodyRow } from './TableBodyRow'

export interface StandardTableProps {
  data: Array<any>
  visibleHeight: number
  visibleWidth: number
  rowHeight: number
  rowWidth: number
  stickyCols: Array<StickyStyle>
  fieldNameToJump?: string
  colDef: Array<TableViewColumnDefinition>
  useIsScrolling?: boolean
  overScanCount?: number
  expandableSectionHeight?: number
  expandedRowKey?: string
  expandedRowComponent?: any
  tableCellFontSize?: any
  showBodyRowBorder?: boolean
  allowMultiSelect?: boolean
  maxSelectionLimit?: number
  setSelectedData?: any
  selectedData?: any
  disabledRowData?: any[]
}

const createItemData = memoize(
  (
    maxSelectionLimit,
    allowMultiSelect,
    showBodyRowBorder,
    dataToVirtualize,
    rowWidth,
    colDef,
    selectedRows,
    onCheckboxClick,
    stickyCols,
    rowHeight,
    expandableSectionHeight,
    expandedRowKey,
    expandedRowComponent,
    disabledRows,
    setDisabledRows,
    tableCellFontSize,
    setSelectedData
  ) => ({
    dataToVirtualize,
    rowWidth,
    colDef,
    selectedRows,
    onCheckboxClick,
    stickyCols,
    rowHeight,
    expandableSectionHeight,
    expandedRowKey,
    expandedRowComponent,
    tableCellFontSize,
    showBodyRowBorder,
    allowMultiSelect,
    maxSelectionLimit,
    setSelectedData,
    disabledRows,
    setDisabledRows
  })
)

export const TableBody: React.SFC<StandardTableProps> = ({
  data,
  fieldNameToJump,
  rowWidth,
  rowHeight,
  visibleHeight,
  visibleWidth,
  colDef,
  stickyCols,
  useIsScrolling,
  overScanCount,
  expandableSectionHeight,
  expandedRowKey,
  expandedRowComponent,
  tableCellFontSize,
  showBodyRowBorder,
  allowMultiSelect,
  maxSelectionLimit,
  setSelectedData,
  selectedData,
  disabledRowData
}) => {
  return useObserver(function useHook() {
    const {
      dataToVirtualize,
      listOuterRefs,
      setTableData,
      setListRef,
      setScrollLeft,
      setListOuterRef,
      onVerticalTableScroll,
      listRefs,
      clearSearchText,
      selectedRows,
      onCheckboxClick,
      headerRef,
      setScrollLeftForOuterRef,
      setPreSelectedRows,
      disabledRows,
      setDisabledRows,
      clearSelectedRows,
      selectedFilterID
    } = React.useContext(TableViewStoreContext) as any

    const handleScroll = e => {
      setScrollLeft(e.target.scrollLeft)
    }

    useEffect(() => {
      if (selectedData?.length) {
        setPreSelectedRows(selectedData)
      }
      if (disabledRowData?.length) {
        setDisabledRows(disabledRowData)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedData, disabledRowData])

    /**
     * To sync horizontal scroll for table body
     * and header
     */
    const containerRef: any = containerRef => {
      if (containerRef) {
        containerRef.addEventListener('scroll', handleScroll)
        setListOuterRef(containerRef)
      }
    }

    useEffect(() => {
      return () => {
        if (listOuterRefs && listOuterRefs.scroll) {
          listOuterRefs.removeEventListener('scroll', handleScroll)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Clear selected checkbox status on component unmount
    useEffect(() => {
      return () => {
        clearSelectedRows()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const listRef: any = ref => {
      if (ref !== null) {
        setListRef(ref)
      }
    }

    useEffect(() => {
      clearSearchText()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if (!isEmpty(data)) {
        const tableClientHeight =
          rowWidth > visibleWidth ? visibleHeight - scrollbarSize() : visibleHeight
        setTableData(
          data,
          fieldNameToJump,
          rowHeight,
          tableClientHeight,
          colDef,
          maxSelectionLimit,
          setSelectedData
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, visibleHeight, selectedFilterID])

    useDeepCompareEffect(() => {
      /**
       * Reset cached indexes when data changes in case of expandable row, so that item
       * size is re-calculated
       */
      if (listRefs && expandedRowKey && expandableSectionHeight) {
        listRefs.resetAfterIndex(0)
      }

      if (
        listOuterRefs &&
        headerRef &&
        listOuterRefs.scrollLeft === 0 &&
        listOuterRefs.scrollLeft !== headerRef.scrollLeft
      ) {
        // GBB-3294: Set table scroll to be same as header after all records are filtered out and added back
        setScrollLeftForOuterRef(headerRef.scrollLeft)
      }
    }, [dataToVirtualize])

    const itemData = createItemData(
      maxSelectionLimit,
      allowMultiSelect,
      showBodyRowBorder,
      dataToVirtualize,
      rowWidth,
      colDef,
      selectedRows,
      onCheckboxClick,
      stickyCols,
      rowHeight,
      expandableSectionHeight,
      expandedRowKey,
      expandedRowComponent,
      disabledRows,
      setDisabledRows,
      null,
      null
    )
    return itemData.dataToVirtualize.length ? (
      <List
        ref={listRef}
        outerRef={containerRef}
        overscanCount={overScanCount ? overScanCount : 1}
        onScroll={onVerticalTableScroll}
        style={{ overflowX: 'hidden' }}
        itemData={itemData}
        height={visibleHeight}
        itemCount={itemData.dataToVirtualize.length}
        itemSize={index =>
          dataToVirtualize?.[index]?.[expandedRowKey!] && expandableSectionHeight
            ? rowHeight + expandableSectionHeight
            : rowHeight
        }
        width={visibleWidth}
      >
        {TableBodyRow}
      </List>
    ) : (
      <div style={{ width: visibleWidth }}>
        <NoRecordFound />
      </div>
    )
  })
}
